import { Box } from '@wix/design-system';
import { Action } from 'history';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigationType } from 'react-router-dom';

import { useSkipFirstEffect } from '../../../chatbot/components/Field/useSkipFirstEffect';
import { Loader } from '../../../common/components/Loader';
import { useFetchArticle } from '../../../common/hooks/useFetchArticle';
import { helpCenterSlice } from '../../../common/reducers/helpCenter';
import {
  selectCurrentArticle,
  selectCurrentArticleLoading,
  selectFullPageSidebarExtended,
} from '../../../common/selectors/helpCenter';
import { useAppDispatch } from '../../../common/store';
import { isSmartphone } from '../../../util';
import { biHelpCenter } from '../../../util/bi';
import { ArticleContainer } from '../ArticleContainer';
import { ArticleSidebarHeader } from './ArticleSidebarHeader';

export const ArticleSidebar: FC = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigationType = useNavigationType();
  const article = useSelector(selectCurrentArticle);
  const articleLoading = useSelector(selectCurrentArticleLoading);
  const sidebarExtended = useSelector(selectFullPageSidebarExtended);
  const [subtitleText, setSubtitleText] = useState<undefined | string>(
    undefined,
  );
  const [backArticlesCount, setBackArticlesCount] = useState(0);

  const onTitleHiddenChange = useCallback(
    (value: boolean) => setSubtitleText(value ? article?.title : undefined),
    [article],
  );

  useFetchArticle();

  useSkipFirstEffect(() => {
    if (navigationType === Action.Push) {
      return setBackArticlesCount(backArticlesCount + 1);
    }

    if (navigationType === Action.Pop) {
      return setBackArticlesCount(backArticlesCount - 1);
    }
  }, [location]);

  useEffect(() => {
    dispatch(helpCenterSlice.actions.setSidebarOpened(true));

    return () => {
      dispatch(helpCenterSlice.actions.setSidebarOpened(false));
    };
  }, []);

  useSkipFirstEffect(() => {
    if (!article) {
      return;
    }

    // TODO:bi fix race condition with state monitor
    Promise.resolve().then(() =>
      // 1119 supposed to be fired since our sidebar display is changing (from expanded window to minimized or vise versa).
      biHelpCenter.logSingleArticlePresented(article.id, article.locale),
    );
  }, [sidebarExtended]);

  if (!article || articleLoading) {
    return (
      <Loader
        dataHook="article__loader"
        height="100%"
        width="100%"
        verticalAlign="middle"
      />
    );
  }

  return (
    <Box direction="vertical" width="100%" align="center">
      <ArticleSidebarHeader
        subtitleText={subtitleText}
        showBackButton={backArticlesCount > 0 || isSmartphone}
      />
      <ArticleContainer
        article={article}
        onTitleHiddenChange={onTitleHiddenChange}
        openCategoriesExternally
        sidebar
      />
    </Box>
  );
};
